import { Link, useNavigate } from "react-router-dom";
import { TrendingUp, Wallet, Percent, Boxes, ShoppingBag, MessageSquare, Crosshair, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { eur, pct } from "@/lib/api";
import { useApi, Kpi, Card, Pill, Score, Empty, Btn } from "@/components/kit";
import { SystemStatus, WaitingActions } from "@/components/Status";
import { PlanList } from "@/pages/AgentPages";

export default function Home() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { data: d } = useApi("/dashboard");
  const { data: plan } = useApi("/daily-plan");
  const { data: opt } = useApi("/optimizer/audit");
  const { data: threads } = useApi("/threads");
  const { data: alerts } = useApi("/alerts?status=unread");
  const opps = (alerts || []).filter((a) => a.type === "OPPORTUNITY");
  return (
    <div className="space-y-6" data-testid="home-page">
      {d?.data_state === "NO_DATA" && <div className="card p-4 text-sm text-[#A0AAB2]" data-testid="home-no-data">Pas encore de données : ajoute tes produits, importe un CSV ou connecte l'extension. Les indicateurs ci-dessous restent vides tant qu'aucune source n'est connectée.</div>}
      {d?.is_demo && <div className="card p-3 text-xs text-[#FFBD36] border-[#FFBD36]/30" data-testid="home-demo-banner">DONNÉES D'EXEMPLE (DEMO) : ces chiffres sont fictifs.</div>}
      <div className="fade-up">
        <div className="mono text-[11px] tracking-[.25em] text-[#7CFF00] mb-2">COCKPIT</div>
        <h1 className="text-3xl sm:text-5xl font-black tracking-tight" data-testid="home-greeting">Bonjour {user?.first_name}</h1>
        <div className="mt-4"><SystemStatus /></div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
        <Kpi label="CA mois" value={d && eur(d.ca_month)} icon={TrendingUp} testid="kpi-ca" />
        <Kpi label="Bénéfice" value={d && eur(d.profit_month)} accent="gold" icon={Wallet} testid="kpi-profit" sub="CA ≠ bénéfice" />
        <Kpi label="Marge" value={d && pct(d.margin_pct)} icon={Percent} testid="kpi-margin" />
        <Kpi label="Stock" value={d && eur(d.stock_value)} icon={Boxes} testid="kpi-stock" />
        <Kpi label="Ventes" value={d?.sales_month} icon={ShoppingBag} testid="kpi-sales" />
        <Kpi label="Messages" value={d?.awaiting_messages} icon={MessageSquare} testid="kpi-messages" sub="en attente" />
        <Kpi label="Opportunités" value={opps.length} icon={Crosshair} testid="kpi-opportunities" />
      </div>
      <div className="card p-6 glow fade-up" data-testid="home-daily-plan">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-black">Que dois-je faire aujourd'hui ?</h2>
            {plan && <p className="text-sm text-[#A0AAB2]">{plan.top5.length} actions · ~{plan.total_minutes_top5} min · impact potentiel estimé {eur(plan.impact_low)} à {eur(plan.impact_high)}</p>}
          </div>
          <Btn onClick={() => nav("/daily-plan")} data-testid="home-open-plan">Plan complet <ArrowRight size={14} /></Btn>
        </div>
        {plan && <PlanList actions={plan.top5} />}
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <WaitingActions />
        <Card title="Opportunités" right={<Link to="/scout" className="text-xs text-[#7CFF00]">Scout →</Link>} testid="home-opportunities">
          {opps.length ? opps.slice(0, 5).map((a) => <div key={a.id} className="py-2 border-b border-white/5 text-sm"><div>{a.title}</div><div className="text-xs text-[#A0AAB2]">{a.body}</div></div>)
            : <Empty text="Lance un scan Scout pour détecter des opportunités."><Btn size="sm" variant="ghost" onClick={() => nav("/scout")}>Ouvrir Scout</Btn></Empty>}
        </Card>
        <Card title="Annonces sous-performantes" right={<Link to="/optimizer" className="text-xs text-[#7CFF00]">Optimizer →</Link>} testid="home-underperforming">
          {(opt || []).filter((o) => o.issues.length).slice(0, 5).map((o) => (
            <div key={o.listing.id} className="py-2 border-b border-white/5 flex items-center justify-between gap-2 text-sm">
              <div className="min-w-0"><div className="truncate">{o.listing.title}</div><div className="text-xs text-[#A0AAB2]">{o.issues[0].WHY}</div></div><Score value={o.score} />
            </div>))}
          {opt && !opt.some((o) => o.issues.length) && <Empty text="Aucune annonce sous-performante." />}
        </Card>
        <Card title="Messages prioritaires" right={<Link to="/messenger" className="text-xs text-[#7CFF00]">Messenger →</Link>} testid="home-priority-messages">
          {(threads || []).filter((t) => t.awaiting_reply).slice(0, 5).map((t) => (
            <div key={t.id} className="py-2 border-b border-white/5 flex items-center justify-between text-sm">
              <div className="min-w-0"><div>{t.buyer_name}</div><div className="text-xs text-[#A0AAB2] truncate">{t.listing?.title}</div></div><Pill value={t.classification} />
            </div>))}
          {threads && !threads.some((t) => t.awaiting_reply) && <Empty text="Aucun message en attente." />}
        </Card>
      </div>
    </div>
  );
}
